import Controller from '@ember/controller';
import { service } from '@ember/service';
import { get } from '@ember/object';
import { task } from 'ember-concurrency';
import { action } from '@ember/object';
import { reads, equal } from 'macro-decorators';

export default class IntegrationsIndexController extends Controller {
  @service state;
  @service router;
  @service featureFlags;
  @service integrationsManager;
  @service flashMessages;
  @service store;

  @reads('state.currentLocation') currentLocation;
  @reads('state.currentCompany') currentCompany;
  @reads('currentCompany.saml.content') samlIntegration;
  @reads('currentCompany.googleApp.content') googleIntegration;
  @reads('currentCompany.oneLogin.content') oneLoginIntegration;
  @reads('currentCompany.oktaIntegration.content') oktaIntegration;
  @reads('currentCompany.hrisIntegration.content') hrisIntegration;
  @equal('currentCompany.scimIntegration.provider', 'onelogin') oneLoginSCIM;
  @equal('currentCompany.scimIntegration.provider', 'okta') oktaSCIM;
  @equal('currentCompany.scimIntegration.provider', 'azure') entraIdSCIM; // microsoft renamed azure to entra id
  @equal('currentCompany.scimIntegration.provider', 'centrify') delineaSCIM; // centrify renamed to delinea
  @reads('currentCompany.scimIntegration.content') scimIntegration;

  get canAccessDirectorySync() {
    const isFeatureGatingEnabled = this.featureFlags.isEnabled('app-store-integrations-feature-gating');
    if (isFeatureGatingEnabled) {
      return this.state.features?.canAccessDirectorySsoIntegrations;
    }
    return this.state.features?.canAccessDirectorySync;
  }

  get canAccessSaml() {
    const isFeatureGatingEnabled = this.featureFlags.isEnabled('app-store-integrations-feature-gating');
    if (isFeatureGatingEnabled) {
      return this.state.features?.canAccessDirectorySsoIntegrations;
    }
    return this.state.features?.canAccessSaml;
  }

  // Master gate for the HRIS feature. When off, the tile is hidden,
  // /employees/hris-settings redirects, and every downstream HRIS getter
  // returns false so a stale employees_sync_provider = 'hris' on the
  // company record doesn't accidentally grey out the other provider tiles
  // in an env where the feature isn't rolled out yet.
  get hrisDirectorySyncEnabled() {
    return this.featureFlags.isEnabled('hris-directory-sync');
  }

  // Active HRIS plugin install for this customer. We deliberately exclude
  // `pending` (wizard started, connection not yet established) — the tile
  // should not flip to "Enable" until the customer has completed the
  // connection step inside the plugin wizard. Backend reaches `active`
  // only after the install wizard's final step persists.
  get hrisPluginInstall() {
    if (!this.hrisDirectorySyncEnabled) return undefined;
    return get(this.model, 'pluginInstalls')?.find((install) => {
      const status = get(install, 'status');
      const name = get(install, 'plugin.name') ?? '';
      return name.startsWith('HRIS') && status === 'active';
    });
  }

  // Pending HRIS plugin install — customer opened the wizard but didn't
  // finish the connection step. Drives the "Configure" tile state so the
  // customer can resume mid-setup instead of restarting.
  get pendingHrisPluginInstall() {
    if (!this.hrisDirectorySyncEnabled) return undefined;
    return get(this.model, 'pluginInstalls')?.find((install) => {
      const status = get(install, 'status');
      const name = get(install, 'plugin.name') ?? '';
      return name.startsWith('HRIS') && status === 'pending';
    });
  }

  get hasPendingHrisInstall() {
    return Boolean(this.pendingHrisPluginInstall);
  }

  get hrisPlugin() {
    const byName = (p) => (get(p, 'name') ?? '').startsWith('HRIS');
    return (
      get(this.model, 'plugins')?.find(byName) ||
      get(this.hrisPluginInstall, 'plugin') ||
      this.store.peekAll('plugin').find(byName)
    );
  }

  get hasHrisInstalled() {
    return Boolean(this.hrisPluginInstall);
  }

  /**
   * HRIS plugin can be installed without claiming the directory-sync slot
   * (e.g. customer using it for time-off only). The truth is
   * `company.employees_sync_provider` — envoy-web owns the provider mutex
   * + sync_mode/sync_state cascade on save. The old
   * `plugin_install.config.trackCompleteDirectorySync` flag was dropped
   * when app-store cut over to companies#update (envoy-app-store#1573 /
   * envoy-web EIP-3541); mirroring that field here silently returned
   * false and greyed out this tile after a successful save round-trip.
   */
  get hrisOwnsDirectorySync() {
    if (!this.hrisPluginInstall) return false;
    return get(this.currentCompany, 'employeesSyncProvider') === 'hris';
  }

  get hasScimIntegration() {
    return this.featureFlags.isEnabled('scim-integration', {
      created_at: get(this.currentCompany, 'createdAt'),
    });
  }

  // this is to return the Active Directory plugin install if it exists
  get activeSyncPluginInstalls() {
    // find plugins in the sync category
    const syncPlugins = get(this.model, 'plugins').filterBy('category', 'sync');
    const pluginInstalls = get(this.model, 'pluginInstalls');

    // find active installs that belong to any plugin in the sync category
    const syncPluginInstalls = pluginInstalls.filter((install) => {
      return syncPlugins.findBy('id', get(install, 'plugin.id')) !== undefined;
    });

    return syncPluginInstalls.filter(function (install) {
      const status = get(install, 'status');
      return status === 'active' || status === 'pending';
    });
  }

  get currentSyncIntegration() {
    let currentIntegration = false;
    const activeSyncPluginInstalls = this.activeSyncPluginInstalls;

    if (this.googleIntegration) {
      currentIntegration = 'Google';
    }

    if (this.oneLoginIntegration || this.oneLoginSCIM) {
      currentIntegration = 'OneLogin';
    }

    if (this.oktaIntegration || this.oktaSCIM) {
      currentIntegration = 'Okta';
    }

    if (this.entraIdSCIM) {
      currentIntegration = 'Entra ID';
    }

    if (this.delineaSCIM) {
      currentIntegration = 'Delinea';
    }

    if (this.hrisOwnsDirectorySync) {
      currentIntegration = 'HRIS';
    }

    // This would say e.g. 'OKTA SCIM' if you installed the SCIM
    // version and then lost priviliges for SCIM.
    if (!this.featureFlags.isEnabled('scim-integration')) {
      if (this.scimIntegration) {
        currentIntegration += ' SCIM';
      }
    }

    if (activeSyncPluginInstalls.length) {
      currentIntegration = get(activeSyncPluginInstalls, 'firstObject.plugin.name');
    }

    return currentIntegration;
  }

  get hasSync() {
    return this.currentSyncIntegration !== false;
  }

  @action
  goToPluginInstallConfig(integration) {
    this.router.transitionTo('employees.scim-settings', { queryParams: { integration } });
  }

  installGoogleAppsTask = task(async () => {
    const { currentCompany } = this.state;
    const integrationsManager = this.integrationsManager;
    // Clean any existing error this.store.unloadAll('google-app');
    this.store.unloadAll('google-app');

    try {
      await integrationsManager.connectGoogleApps(currentCompany);
      currentCompany.reload();
      this.router.transitionTo('employees.scim-settings');
    } catch (response) {
      let message = "Sorry, we couldn't connect your account.";
      if (response?.errors) {
        const [error] = response.errors;
        if (error) {
          message = error.messages.join(', ');
        }
      }
      this.flashMessages.showAndHideFlash('error', message);
    }
  });

  installOneLoginTask = task(async () => {
    const { currentCompany } = this.state;
    this.store.createRecord('one-login', { company: currentCompany });
    await this.router.transitionTo('employees.scim-settings', {
      queryParams: { from: 'employees.integrations', integration: 'one-login' },
    });
  });

  installOktaTask = task(async () => {
    const { currentCompany } = this.state;
    this.store.createRecord('okta-integration', { company: currentCompany });
    await this.router.transitionTo('employees.scim-settings', {
      queryParams: { from: 'employees.integrations', integration: 'okta' },
    });
  });

  installScimTask = task(async (provider) => {
    const { currentCompany } = this.state;
    this.store.createRecord('scim-integration', { company: currentCompany, provider }).save();
    await this.router.transitionTo('employees.scim-settings', {
      queryParams: { from: 'employees.integrations', integration: 'scim' },
    });
  });

  // "Install" CTA — customer has no HRIS plugin install yet. Sends them
  // to the app-store marketplace entry for the HRIS plugin.
  installHrisTask = task(async () => {
    const pluginKey = get(this.hrisPluginInstall, 'plugin.key') || get(this.hrisPlugin, 'key');
    if (pluginKey) {
      await this.router.transitionTo('apps.dynamic', `by/${pluginKey}`);
      return;
    }
    await this.router.transitionTo('apps.dynamic', 'integrations');
  });

  // "Enable" / "Disable" CTAs — HRIS plugin is installed; customer manages
  // the directory-sync slot on the dedicated sync-settings page rather
  // than via the plugin install wizard. Same destination for both CTAs;
  // the page's toggle reflects the current state.
  goToHrisSettingsTask = task(async () => {
    await this.router.transitionTo('employees.hris-settings');
  });

  installSamlTask = task(async () => {
    const { currentCompany } = this.state;
    this.store.createRecord('saml', { company: currentCompany });
    await this.router.transitionTo('employees.scim-settings', {
      queryParams: { from: 'employees.integrations', integration: 'saml' },
    });
  });
}
